import { styled } from 'styled-components';
import { theme } from '../styles';
import Swapper, { SwapChild } from './Swapper';

const Button = ({
  type = 'button',
  style = 'outline',
  color = 'mutedColor',
  size = 'md',
  width = 'full',
  justify = 'center',
  icon = false,
  to,
  target,
  state,
  initial,
  animate,
  exit,
  transition,
  onClick,
  className,
  children,
  swapChildren,
}) => {
  let height = `calc(${theme.sizeH5} + (2 * ${theme.spaceSm}))`;

  if (size === 'lg') {
    height = `calc(${theme.sizeH6} + (2 * ${theme.spaceMd}))`;
  }
  
  if (icon) {
    return (
      <IconWrapper
        type={type}
        $style={style}
        $color={color}
        $size={size}
        $width={width}
        height={height}
        to={to}
        target={target}
        state={state}
        initial={initial}
        animate={animate}
        exit={exit}
        transition={transition}
        onClick={onClick}
        className={className}
        swapChildren={swapChildren}
      >
        {children}
      </IconWrapper>
    );
  } else {
    return (
      <ButtonWrapper
        type={type}
        $style={style}
        $color={color}
        $size={size}
        $width={width}
        $justify={justify}
        to={to}
        target={target}
        state={state}
        height={height}
        initial={initial}
        animate={animate}
        transition={transition}
        exit={exit}
        onClick={onClick}
        className={className}
        swapChildren={swapChildren}
      >
        {children}
      </ButtonWrapper>
    );
  }
};

const BaseButton = styled(Swapper)`
  position: relative;
  margin: 0 auto;
  justify-content: unset;

  ${props => {
    if (props.$style === 'outline') {
      return `
        color: ${props.theme[props.$color]};
        background-color: transparent;
        border-color: ${props.theme[props.$color]};
        &:hover {
          color: ${props.theme.backgroundColor};
          background-color: ${props.theme[props.$color]};
        }
      `;
    } else if (props.$style === 'fill') {
      return `
        color: ${props.theme.backgroundColor};
        background-color: ${props.theme[props.$color]};
        border-color: ${props.theme[props.$color]};
        &:hover {
          border-color: ${props.theme.brand};
          background-color: ${props.theme.brand};
        }
      `;
    }
  }}

  border-width: 2px;
  border-style: solid;

  ${SwapChild} {
    width: 100%;
    justify-content: ${props => props.$justify};
  }

  ${SwapChild}:first-of-type {
    margin-top: -1px;
  }
  &:hover {
    ${SwapChild}:first-of-type {
      margin-top: calc((-1 * ${props => props.height}) - 1px);
    }
  }

  &:before {
    position: absolute;
    top: calc(50% + -10 * ${props => props.height});
    left: calc(50% + -10 * ${props => props.height});
    width: calc(20 * ${props => props.height});
    height: calc(20 * ${props => props.height});
    border-radius: ${props => props.theme.borderFull};
    content: '';
    border: 1px solid ${props => props.theme.brand};
    transform-origin: center center;
    transition: border ${props => props.theme.transitionLong};
    z-index: -1;
    box-sizing: border-box;
  }

  &:hover {
    &:before {
      border-width: calc(10 * ${props => props.height});
    }
  }

  &:active {
    scale: 0.98;
    box-shadow: 0 0 0 4px ${props => props.theme.backgroundColor},
      0 0 0 6px ${props => props.theme.brand + props.theme.clear50};
  }
`;

export const ButtonIcon = styled.span`
  height: ${props => props.theme.sizeH3};
  width: ${props => props.theme.sizeH3};
  margin: -${props => props.theme.sizeBody};
  margin-left: 0;

  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-content: center;
  

  background-color: ${props => props.theme.backgroundColor};
  border-radius: ${props => props.theme.borderFull};

  svg {
    height: ${props => props.theme.sizeH5};
    width: ${props => props.theme.sizeH5};

    color: ${props => props.theme.brand};
  }
`;

export const ButtonTextIcon = styled(ButtonIcon)`
  height: auto;
  width: auto;
  min-height: unset;
  min-width: unset;

  padding: ${props => props.theme.paddingSm};
  margin-right: -${props => props.theme.spaceSm};
  font-size: ${props => props.theme.sizeSmall};
  color: ${props => props.theme.backgroundColor};

  background-color: ${props => props.theme.brand};
`;

const ButtonWrapper = styled(BaseButton)`
  width: ${props => (props.$width === 'full' ? '100%' : 'fit-content')};
  ${SwapChild} {
    justify-content: ${props => props.$justify};
    ${props => {
      if (props.$size === 'sm') {
        return `
        padding: ${props.theme.spaceXs} ${props.theme.spaceSm};
        font-size: ${props.theme.sizeSmall};
      `;
      } else if (props.$size === 'md') {
        return `
        padding: ${props.theme.spaceSm} ${props.theme.spaceMd};
        font-size: ${props.theme.sizeBody};
        `;
      } else if (props.$size === 'lg') {
        return `
        padding: ${props.theme.spaceMd} ${props.theme.spaceLg};
        font-size: ${props.theme.sizeH6};
      `;
      }
    }}
  }

  ${SwapChild}:last-of-type ${ButtonTextIcon} {
    color: ${props => props.theme[props.$color]};
    background-color: ${props => props.theme.backgroundColor};
  }
`;

const IconWrapper = styled(BaseButton)`
  height: ${props => props.height};
  width: ${props => props.height};

  justify-content: unset;

  ${SwapChild} {
    ${props => {
      if (props.$size === 'sm') {
        return `
        svg {
          height: ${props.theme.sizeBody};
          width: ${props.theme.sizeBody};
        }
      `;
      } else if (props.$size === 'md') {
        return `
        svg {
          height: ${props.theme.sizeH6};
          width: ${props.theme.sizeH6};
        }
        `;
      } else if (props.$size === 'lg') {
        return `
        svg {
          height: ${props.theme.sizeH5};
          width: ${props.theme.sizeH5};
        }
      `;
      }
    }}
  }
`;

export default Button;
